import { datadogLogs } from '@datadog/browser-logs'

/**
 * Datadog Logging Utility
 *
 * Provides simplified logging methods for Datadog browser-logs integration.
 * Focuses on error logging with custom attributes and context.
 */

/**
 * Log levels supported by Datadog
 */
export const LOG_LEVELS = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
}

/**
 * Base logging function that all other methods use
 * @param {string} message - The log message
 * @param {Object} attributes - Additional attributes to include
 * @param {string} level - Log level (debug, info, warn, error)
 * @param {Error} error - Error object if applicable
 */
const log = (
  message,
  attributes = {},
  level = LOG_LEVELS.INFO,
  error = null
) => {
  let logAttributes = {}

  try {
    // Add timestamp and common attributes
    logAttributes = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      ...attributes,
    }

    // Add error details if provided
    if (error) {
      logAttributes.error = {
        name: error.name,
        message: error.message,
        stack: error.stack,
        ...logAttributes.error, // Allow overriding error details
      }
    }

    datadogLogs.logger.log(message, logAttributes, level, error)
  } catch (err) {
    // Log the error for debugging purposes but don't let it affect app functionality
    // eslint-disable-next-line no-console
    if (console.error) {
      // eslint-disable-next-line no-console
      console.error('Failed to log to Datadog:', err)
    }
  }
}

/**
 * Log an error with custom attributes
 * @param {string} message - Error message
 * @param {Object} options - Logging options
 * @param {Error} options.error - Error object
 * @param {Object} options.attributes - Additional attributes
 */
export const logError = (message, options = {}) => {
  const { error, attributes = {} } = options
  log(message, attributes, LOG_LEVELS.ERROR, error)
}

/**
 * Log a warning with custom attributes
 * @param {string} message - Warning message
 * @param {Object} options - Logging options
 * @param {Object} options.attributes - Additional attributes
 */
export const logWarning = (message, options = {}) => {
  const { attributes = {} } = options

  log(message, attributes, LOG_LEVELS.WARN)
}

/**
 * Log info with custom attributes
 * @param {string} message - Info message
 * @param {Object} options - Logging options
 * @param {Object} options.attributes - Additional attributes
 */
export const logInfo = (message, options = {}) => {
  const { attributes = {} } = options

  log(message, attributes, LOG_LEVELS.INFO)
}

/**
 * Log debug information
 * @param {string} message - Debug message
 * @param {Object} options - Logging options
 * @param {Object} options.attributes - Additional attributes
 */
export const logDebug = (message, options = {}) => {
  const { attributes = {} } = options

  log(message, attributes, LOG_LEVELS.DEBUG)
}

export default {
  logError,
  logWarning,
  logInfo,
  logDebug,
  LOG_LEVELS,
}
